import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import equal from "fast-deep-equal";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo } from "react";
import type { SmartViewWidget } from "../../../../../api/types/dashboardsTypes.ts";
import type { ConditionField } from "../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../reporting/sv/contexts/BiCommonDataContext.ts";
import { getFieldCaption } from "../../../reporting/sv/utilities/Utilities.tsx";
import { getCurrentLocale } from "../../../reporting/utils/dateUtils.ts";
import { useDashboardContext } from "../../contexts/DashboardContext.tsx";
import type { WidgetFilter } from "../../dashboard.types.ts";

interface Props {
  widget: SmartViewWidget;
  conditions: ConditionField[];
}

const SmartViewWidgetWrapper = ({ children, widget, conditions }: PropsWithChildren<Props>) => {
  const { onConditionUpdate, dataSetId, isMultiBook, books } = useBiCommonDataContext();
  const { addFilters, activeFilters } = useDashboardContext();
  const parameters = useMemo(() => conditions.filter((c) => c.config.parameter === true), [conditions]);

  useEffect(() => {
    const widgetFilters = parameters.map((p): WidgetFilter => ({
      id: p.meta.name,
      caption: getFieldCaption(p),
      dataType: getDataType(p.meta.type),
      widgetId: widget.widgetId,
      options: p.meta.dictionary || {},
      dataSetId,
      availableFields: p.meta.availableFields,
      isMultiBook,
      books,
      displayField: p.meta.displayValues[0],
      valueField: p.meta.keyFieldName,
    }));
    addFilters(widgetFilters);
  }, [parameters, widget, addFilters, dataSetId, isMultiBook, books]);

  useEffect(() => {
    parameters.forEach((c) => {
      const existingFilter = activeFilters.find((f) => f.id === c.meta.name);
      const copy = structuredClone(c.config);
      if (existingFilter && !equal(c.config.filter.values, existingFilter.values)) {
        copy.filter.values = [...(existingFilter.values as string[])];
      }
      onConditionUpdate(c, { config: copy });
    });
  }, [activeFilters, onConditionUpdate, widget.widgetId, parameters]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocale()}>
      {children}
    </LocalizationProvider>
  );
};

export default SmartViewWidgetWrapper;

const getDataType = (type: ItemDataType) => {
  switch (type) {
    case ItemDataType.Date:
      return "date";
    case ItemDataType.Numeric:
      return "number";
    default:
      return "text";
  }
};
